// ─────────────────────────────────────────────────────────────────────────────
// CEO Command Center — same-origin authority request paths (Task 8.2 / 8.3).
// Spec: executive-authority-rbac.
//
// The SINGLE source of truth for every authority/identity/oversight request path.
// Each builder returns a path beginning with `/api/` to be used against the shared
// axios client whose base is intentionally EMPTY (same-origin) — so a request
// NEVER resolves to `/api/api/` (EA-10, Req 13.1/13.2). Dynamic segments are
// encodeURIComponent-escaped here so callers can't accidentally build a malformed
// or double-prefixed path. Pure + deterministic → unit/property-testable offline.
// ─────────────────────────────────────────────────────────────────────────────

import type { OversightDomain } from './types';

export const authorityPaths = {
  /** Caller's derived tier/attribution (never a raw role). */
  identity: (): string => '/api/authority/me',

  systemHealth: (): string => '/api/admin/system-health',

  org: (): string => '/api/admin/org',
  orgSetting: (key: string): string => `/api/admin/org/${encodeURIComponent(key)}`,

  audit: (action?: string): string =>
    action ? `/api/admin/audit?action=${encodeURIComponent(action)}&limit=200` : '/api/admin/audit?limit=200',

  apiKeys: (): string => '/api/admin/api-keys',
  revokeApiKey: (id: string): string => `/api/admin/api-keys/${encodeURIComponent(id)}/revoke`,

  backups: (): string => '/api/admin/backups',

  presence: (): string => '/api/admin/presence',

  oversight: (domain: OversightDomain): string => `/api/admin/oversight/${encodeURIComponent(domain)}`,
  oversightMedia: (filename: string): string => `/api/admin/oversight/media/${encodeURIComponent(filename)}`,
} as const;

export type AuthorityPaths = typeof authorityPaths;
